/**
 * This module exposes VirtualizedList / useVirtualizeItem to improve rendering performance by rendering a
 * skeleton for any component in the list outside of the view port. For the most part, this functionality
 * is already provided by the `useHasBeenInView` hook,  but for use cases like the the Mediocre Editor
 * framework, we need further guarantees around selection / scroll position which this module provides.
 *
 *  Guarantees:
 *  - initial render will use all virtualized skeleton items to improve initial page load.
 *  - if the list is unmounted and re-mounted, any skeletons will receive its last measured height,
 *    to ensure existing code to restore scroll position jumps to the right place.
 *  - newly inserted items will rendered as non-skeletons immediately
 *    to ensure existing code to set DOM selection continues to work.
 *  - any items that are scrolled into view are then always kept as rendered.
 *
 * This virtualization strategy will only help with performance if the skeleton render is significantly
 * cheaper than the actual render.
 *
 * NOTE: Native Ctrl-F will only work over content that is rendered, which is often unexpected from a user's
 * perspective. See: BNCH-35363
 *
 * Example Usage:
 *   ```js
 *   function MyList() {
 *     // Previously: `<>{props.items.forEach((item) => <MyItem item={item} />}</>`
 *     return (
 *       <VirtualizedList>
 *          {props.items.forEach((item) => <MyItemWrapper item={item} />}
 *       </VirtualizedList>
 *     );
 *   }
 *
 *   // Unchanged component
 *   function MyItem({item}): JSX.Element {
 *     return <div>{renderSomewhatExpensiveItem(item)}</div>;
 *   }
 *
 *   // New component
 *   function MyItemSkeleton({item, height}): JSX.Element {
 *      return (
 *        <div style={{height}}>
 *          my skeleton for {item.id}
 *        </div>
 *      );
 *   }
 *
 *   // New component
 *   function MyItemWrapper(item) {
 *     const {ref, height, shouldVirtualize} = useVirtualizeItem(
 *        item,
 *        item.cid,
 *        true, // enabled
 *        () => getMyHeightEstimate(item)
 *     )
 *     return (
 *       <div ref={ref}>
 *         {shouldVirtualize
 *           ? <MySkeleton item={item} height={height} />
 *           : <MyItem item={item} />}
 *       </div>
 *     )
 *   }
 *   ```
 */
import React, {MutableRefObject, Ref, useCallback, useEffect, useRef, useState} from 'react';

import BenchlingConfig from 'BenchlingConfig';
import {makeReactContext} from 'lib/makeReactContext';
import useHasBeenInView from 'ui-library/hooks/useHasBeenInView';
import useResizeObserver from 'ui-library/hooks/useResizeObserver';
import {Optional} from 'util/Types';

import {useMemoryProfiler} from './MemoryProfiler';
import {isRevirtualizationEnabled, useIsNowInView} from './Revirtualization';
import {getVirtualizationState, VirtualizationState} from './VirtualizationState';

/**
 * Concern 1: Store component height after updates to restore on re-mount
 */

// Cache of actual item heights after non-virtualized render.
export const actualHeightsByItemId: {[id: string]: number} = {};

// Locally cache the height of an item whenever the item updates.
/** @package */
export function useActualHeight({shouldVirtualize, itemId}: {shouldVirtualize: boolean; itemId: string}): {
  ref: (instance: HTMLElement | null) => void;
  actualHeight: number | undefined;
} {
  const effectRef = useRef<HTMLElement | null>(null);

  // If the id of an item changes, update the cache.
  useEffect(() => {
    if (!shouldVirtualize && effectRef.current) {
      const {height} = effectRef.current.getBoundingClientRect();
      actualHeightsByItemId[itemId] = height;
    }
  }, [itemId, shouldVirtualize]);

  // If the element corresponding to an item has a resize observed, update the cache.
  const onResize = useCallback(
    ({height}) => {
      if (!shouldVirtualize && height !== undefined) {
        actualHeightsByItemId[itemId] = height;
      }
    },
    [shouldVirtualize, itemId]
  );
  const {ref: resizeRef} = useResizeObserver({onResize});
  const ref = useCallback(
    (node) => {
      resizeRef(node);
      effectRef.current = node;
    },
    [resizeRef, effectRef]
  );

  return {
    ref,
    actualHeight: actualHeightsByItemId[itemId],
  };
}

export function resetVirtualizationCacheForTests(): void {
  for (const key of Object.keys(actualHeightsByItemId)) {
    delete actualHeightsByItemId[key];
  }
}

/**
 * Concern 2: Detecting if an item was newly inserted.
 */

type RenderRef = MutableRefObject<boolean>;

const InitialRenderContext = makeReactContext<Optional<RenderRef>>(() => null, 'InitialRenderContext');

function useInitialRenderRef(documentVersionCid?: string): RenderRef {
  const initialRenderRef = useRef(true);
  const documentVersionCidRef = useRef(documentVersionCid);

  // If the documentVersionCid changes, we need to reset the initial render ref so that all the new noteparts
  // will be virtualized if applicable.
  if (documentVersionCidRef.current !== documentVersionCid && documentVersionCid) {
    initialRenderRef.current = true;
    documentVersionCidRef.current = documentVersionCid;
  }

  useEffect(() => {
    initialRenderRef.current = false;
  });

  return initialRenderRef;
}

/** @package */
export function useHasNotDevirtualized(shouldVirtualize: boolean): boolean {
  const hasDevirtualized = useRef(false);
  if (!shouldVirtualize) {
    hasDevirtualized.current = true;
  }
  return !hasDevirtualized.current;
}

/** @package */
export function useIsNewlyInserted(): boolean {
  const isParentInitialRenderRef: Optional<RenderRef> = InitialRenderContext.useContext();
  const isChildInitialRenderRef: RenderRef = useInitialRenderRef();
  return (
    isParentInitialRenderRef != null && !isParentInitialRenderRef.current && isChildInitialRenderRef.current
  );
}

function useVirtualizationDebugger(hasVirtualizationSupport: boolean): {
  forceVirtualization: boolean;
  ref: React.MutableRefObject<Element | undefined>;
} {
  const ref = useRef<Element>();
  /* eslint-disable react-hooks/rules-of-hooks */
  // ELN_ENABLE_VIRTUALIZATION_DEBUGGER does not change after page load, so it's safe to use hooks conditionally.
  if (!BenchlingConfig.ELN_ENABLE_VIRTUALIZATION_DEBUGGER) {
    return {forceVirtualization: false, ref};
  }
  const [forceVirtualization, setForceVirtualization] = useState(true);
  const onWindowMouseMove = useCallback(
    (e) => {
      if (!ref.current) {
        return;
      }
      const {top, bottom} = ref.current.getBoundingClientRect();
      const newForceVirtualization = e.clientY < top || e.clientY > bottom;
      if (newForceVirtualization !== forceVirtualization) {
        setForceVirtualization(newForceVirtualization);
      }
    },
    [forceVirtualization]
  );
  useEffect(() => {
    if (hasVirtualizationSupport) {
      window.addEventListener('mousemove', onWindowMouseMove);
    }
    return () => {
      window.removeEventListener('mousemove', onWindowMouseMove);
    };
  }, [hasVirtualizationSupport, onWindowMouseMove]);
  /* eslint-enable react-hooks/rules-of-hooks */
  return {forceVirtualization, ref};
}

function useMeetsDevirtualizationCriteria(
  enabled: boolean,
  revirtualizationEnabled: boolean
): {
  ref: (node?: Element | null) => void;
  meetsDevirtualizationCriteria: boolean | undefined;
} {
  const useMeetsDevirtualizationCriteria = revirtualizationEnabled ? useIsNowInView : useHasBeenInView;
  const result = useMeetsDevirtualizationCriteria(enabled);
  const ref = result.ref;
  const meetsDevirtualizationCriteria = revirtualizationEnabled
    ? (result as ReturnType<typeof useIsNowInView>).inView
    : (result as ReturnType<typeof useHasBeenInView>).hasBeenInView;
  return {ref, meetsDevirtualizationCriteria};
}

/**
 * Concern 3: Core functionality, composed into re-usable components.
 */

/** @package */
export function useVirtualizationState(enabled: boolean): {
  ref: (node?: Element | null) => void;
  virtualizationState: VirtualizationState;
} {
  const revirtualizationEnabled = isRevirtualizationEnabled();
  const doesFileSupportVirtualization = VirtualizationEnabledContext.useContext();
  const isNewlyInserted = useIsNewlyInserted();
  const {ref: observerRef, meetsDevirtualizationCriteria} = useMeetsDevirtualizationCriteria(
    enabled,
    revirtualizationEnabled
  );
  const hasVirtualizationSupport =
    meetsDevirtualizationCriteria !== undefined && doesFileSupportVirtualization;
  const {forceVirtualization, ref: debugRef} = useVirtualizationDebugger(hasVirtualizationSupport);
  // _useNeverRevirtualize is needed to ensure that if we decide to render a non-skeleton on initial mount,
  // (based on isNewlyInserted), and a re-render happens before the `useHasBeenInView`'s IntersectionObserver
  // runs its first observation, that we don't revert from the non-skeleton to a skeleton, which would notably
  // cause us to lose selection.
  const mayVirtualize =
    // Virtualization is enabled and the prerequisite browser capability is detected
    hasVirtualizationSupport &&
    // If it hasn't been in view yet, render a skeleton...
    !meetsDevirtualizationCriteria &&
    // ...unless, it was /just/ inserted, since we will want to set the cursor inside of this component,
    // on its first render.
    // TODO(BNCH-36335): This strategy handles the main usability issue, but fails to handle:
    //   1. Fast keyboard navigation (arrow keys) of document can get stuck on skeletons.
    //   2. On document load, we want to put the cursor at the start of the document.
    !isNewlyInserted;
  const hasNotDevirtualized = useHasNotDevirtualized(mayVirtualize);

  // Recipe from https://github.com/thebuilder/react-intersection-observer/issues/186
  const ref = useCallback(
    (node) => {
      observerRef(node);
      debugRef.current = node;
    },
    [observerRef, debugRef]
  );

  const virtualizationState =
    hasVirtualizationSupport && forceVirtualization
      ? VirtualizationState.VIRTUALIZED
      : getVirtualizationState(revirtualizationEnabled, mayVirtualize, hasNotDevirtualized);

  return {ref, virtualizationState};
}

export interface VirtualizedListProps {
  children: React.ReactNode;
  documentType: string;
  documentVersionCid: string;
  isVirtualizationEnabled: boolean;
}

const VirtualizationEnabledContext = makeReactContext<boolean>(() => false, 'VirtualizationEnabledContext');

export function VirtualizedList(props: VirtualizedListProps): JSX.Element {
  // TODO(BNCH-36335): Should we also reset _useNeverRevirtualize?
  const initialRenderRef = useInitialRenderRef(props.documentVersionCid);
  useMemoryProfiler(props.documentType);
  return (
    <VirtualizationEnabledContext.Provider value={props.isVirtualizationEnabled}>
      <InitialRenderContext.Provider value={initialRenderRef}>{props.children}</InitialRenderContext.Provider>
    </VirtualizationEnabledContext.Provider>
  );
}

/**
 * Hook for enabling virtualization if item is outside of viewport.
 *
 * @param item - item to be rendered (only used to detect when height needs to recomputed
 * @param itemId - id that uniquely identifies an item across mounts
 *    - only used as a key for restoring heights
 *    - must only be unique amongst other items of the same type within the current document
 * @param enabled - opt-out of virtualization and always render items.
 * @param getVirtualizedHeight - height to render a skeleton; should closure the corresponding `item`.
 * @returns - {ref, height, shouldVirtualize} - see top-level module documentation for example usage.
 *   - `ref` should be assigned to a parent <div> that wraps the virtualized element
 *   - `height` the component should be rendered with
 *   - `shouldVirtualize` indicates if the component should be rendered as a skeleton vs fully rendered
 */
export function useVirtualizeItem<TItem>(
  item: TItem,
  itemId: string,
  enabled: boolean,
  getVirtualizedHeight: () => number
): {ref: Ref<HTMLDivElement>; height: number | undefined; virtualizationState: VirtualizationState} {
  const {ref: observerRef, virtualizationState} = useVirtualizationState(enabled);
  const shouldVirtualize = virtualizationState !== VirtualizationState.DEVIRTUALIZED;

  // Anytime we've rendered after the component has changed, store an updated snapshot of the height to
  // ensure that on a re-mount when restoring scroll position, that the virtualized heights are accurate.
  const {ref: measureHeightRef, actualHeight} = useActualHeight({shouldVirtualize, itemId});
  const height = shouldVirtualize ? (actualHeight ?? getVirtualizedHeight()) : undefined;

  // Recipe from https://github.com/thebuilder/react-intersection-observer/issues/186
  const ref = useCallback(
    (node) => {
      observerRef(node);
      measureHeightRef(node);
    },
    [observerRef, measureHeightRef]
  );

  return {ref, height, virtualizationState};
}
